"use client";

import dynamic from "next/dynamic";
import React, {useEffect, useState} from 'react';
import Link from 'next/link';
import NavbarMobile from "@/components/NavBarMobile";
import Image from "next/image";
import {Badge} from "@/components/ui/badge";
import {VeicoloData} from "@/interface/VeicoloData";
import {formatPrice} from "@/utils/format-price";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import Cookies from "js-cookie";
import {useStoredCart} from "@/hooks/useStoredCart";
import {CONTACT_PHONE_HREF} from "@/lib/public-config";
import {Search, X} from "lucide-react";

const DeferredFormLead = dynamic(() => import("@/components/FormLead"), { ssr: false });
const DeferredSearchBoxHome = dynamic(() => import("@/components/Home/SearchBoxhome"), { ssr: false });


export const navigationConfig = [
    {
        type: 'link',
        href: '/parco-veicoli',
        label: 'Parco Auto',
        hrefLang: 'it'
    },
    {
        type: 'link',
        href: '/offerta-last-minute',
        label: 'Offerte last minute',
        hrefLang: 'it',
        className: '!text-[#D20000] hover:!text-[#D20000]'
    },
    {
        type: 'link',
        href: '/rent-to-buy',
        label: 'Rent to Buy',
        hrefLang: 'it'
    },
    {
        type: 'link',
        href: '/vendi-la-tua-auto/permuta',
        label: 'Vendi',
        hrefLang: 'it'
    },
    {
        type: 'dropdown',
        title: 'Diventa Partner',
        items: [
            {
                label: 'Per commercianti',
                href: '/diventa-partner-commercianti',
                isExternal: false,
                hrefLang: 'it'
            },
            {
                label: 'Per noleggiatori',
                href: '/diventa-partner',
                isExternal: false,
                hrefLang: 'it',
            },
        ]
    },
    {
        type: 'dropdown',
        title: 'Altro',
        items: [
            {
                label: 'Convenzioni',
                href: '/convenzioni',
                isExternal: false,
                hrefLang: 'it'
            },
            {
                label: 'Area commercianti',
                href: 'https://consorzioauto.com',
                isExternal: true,
                hrefLang: 'it'
            },
            {
                label: 'FAQ',
                href: '/faq',
                isExternal: false,
                separator: true,
                hrefLang: 'it'
            },
            {
                label: 'Chi siamo',
                href: '/chi-siamo',
                isExternal: false,
                hrefLang: 'it'
            },
        ]
    }
];

interface NavbarProps {
    showEllipse?: boolean | undefined;
    isAuthenticated?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({showEllipse = false, isAuthenticated = false}) => {
    const [cartOpen, setCartOpen] = useState(false);
    const { vehicles: vehiclesInCart, count: cartCount } = useStoredCart();
    const router = useRouter();

    const [isScrolled, setIsScrolled] = useState(false);

    const [menuOpen, setMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    const [leadFrom, setLeadFrom] = useState(false);
    const [telephone, setTelephone] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const [userData, setUserData] = useState<any>(null);

    const checkoutUrl = userData?.infoUtentiTipo === "COM" ? "/checkout-b2b" : "/checkout";
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentOrder = searchParams?.get("order") || "";

    useEffect(() => {
        setIsClient(true);
        try {
            const raw = Cookies.get("userData");
            if (raw) setUserData(JSON.parse(raw));
        } catch {}
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        // Add the scroll event listener when the component mounts
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const toggleCart = () => {
        setCartOpen(!cartOpen);
        setSearchOpen(false);
    };

    const handleAccount = () => {
        if (pathname === '/diventa-partner-commercianti' || pathname === '/diventa-partner') {
            window.location.href = 'https://www.consorzioauto.com/';
            return;
        }

        if (userData?.code) {
            window.location.href = 'https://consorzioauto.com/dashboard';
            return;
        }

        if (isAuthenticated) {
            router.push('/account/attivita');
        } else {
            router.push('/auth/accedi');
        }
    };

    const backOfficeName = userData?.fullname || userData?.name || userData?.nome;

    const SimpleLink = ({href, label, isScrolled, hrefLang = 'it', className = ''}: any) => {
        const resolvedHref = href === "/parco-veicoli" && currentOrder
            ? `${href}?order=${encodeURIComponent(currentOrder)}`
            : href;

        return (
        <Link
            hrefLang={hrefLang}
            href={resolvedHref}
            className={`text-lg font-medium transition-colors duration-300 hover:text-black hover:underline ${
                isScrolled ? 'text-gray-800' : 'text-gray-800'
            } ${className}`}
        >
            {label}
        </Link>
        );
    };


    const DynamicDropdown = ({title, items, isScrolled}: any) => {
        const [isOpen, setIsOpen] = useState(false);
        const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);  // ➕ NUOVO

        const handleMouseEnter = () => {              // ➕ NUOVO
            if (timeoutId) {                         // ➕ Cancella timeout esistente
                clearTimeout(timeoutId);
                setTimeoutId(null);
            }
            setIsOpen(true);
        };

        const handleMouseLeave = () => {             // ➕ NUOVO
            const id = setTimeout(() => {            // ➕ Delay di 300ms
                setIsOpen(false);
            }, 300);
            setTimeoutId(id);
        };

        return (
            <div
                className="relative"
                onMouseEnter={handleMouseEnter}      // 🔄 Nuova funzione
                onMouseLeave={handleMouseLeave}      // 🔄 Nuova funzione
            >
                {/* Trigger del menu */}
                <button
                    className={`text-lg font-medium transition-colors duration-300 hover:text-black hover:underline flex items-center space-x-1 ${
                        isScrolled ? 'text-gray-800' : 'text-gray-800'
                    }`}
                >
                    <span>{title}</span>
                    <svg
                        className={`w-4 h-4 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>

                {/* Menu dropdown */}
                {isOpen && (
                    <div
                        className="absolute top-full left-0 mt-2 w-64 bg-white/90 border border-gray-200 rounded-lg shadow-lg z-50">
                        <div className="py-2">
                            {items.map((item: any, index: any) => (
                                <div key={index}>
                                    {item.isExternal ? (
                                        // Link esterno
                                        <a
                                            href={item.href}
                                            target={item.target || "_blank"}
                                            rel={item.rel || "noopener noreferrer"}
                                            className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                        >
                                            <span>{item.label}</span>
                                            {item.isExternal && (
                                                <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor"
                                                     viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                                </svg>
                                            )}
                                        </a>
                                    ) : (
                                        // Link interno
                                        <Link
                                            hrefLang={item.hrefLang || 'it'}
                                            href={item.href}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                        >
                                            {item.label}
                                        </Link>
                                    )}
                                    {/* Separatore opzionale */}
                                    {item.separator && index < items.length - 1 && (
                                        <hr className="my-1 border-gray-200"/>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <nav className={`fixed max-lg:hidden top-0 left-0 w-full z-50 transition-all duration-300 ease-in-out ${
                isScrolled ? 'backdrop-blur-md bg-white/30' : 'bg-transparent'
            }`}>
                <div className="w-full px-6">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo */}
                        <div className="flex-shrink-0">
                            <Link href='/'>
                                <Image
                                    src={showEllipse && !isScrolled ? "/logo_bianco.png" : "/logo-vettoriale.svg"}
                                    alt="Gruppo Piccirillo logo"
                                    width={70} // Specifica la larghezza
                                    height={70} // Specifica l'altezza
                                    priority={true} // Precarica l'immagine perché è un elemento critico nella UI
                                    className="h-10 w-auto" // Tailwind per aggiustare ulteriormente lo stile
                                />
                            </Link>
                        </div>

                        {/* Menu principale centrato */}
                        <div className="flex-1 flex justify-center">
                            <div className="flex items-center space-x-12">
                                {navigationConfig.map((item, index) => (
                                    <div key={index}>
                                        {item.type === 'dropdown' ? (
                                            <DynamicDropdown
                                                title={item.title}
                                                items={item.items}
                                                isScrolled={isScrolled}
                                            />
                                        ) : (
                                            <SimpleLink
                                                href={item.href}
                                                label={item.label}
                                                isScrolled={isScrolled}
                                                hrefLang={item.hrefLang || 'it'}
                                                className={item.className}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sezione destra con icone */}
                        <div className="flex items-center gap-4 relative">

                           <DeferredSearchBoxHome className="!py-0.5 mx-2 max-2xl:hidden"/>

                            <div className="relative hidden max-2xl:block">
                                <button
                                    type="button"
                                    title="Cerca auto"
                                    aria-label={searchOpen ? "Chiudi ricerca auto" : "Apri ricerca auto"}
                                    aria-expanded={searchOpen}
                                    onClick={() => {
                                        setCartOpen(false);
                                        setSearchOpen((open) => !open);
                                    }}
                                    className={`flex h-9 w-9 items-center justify-center rounded-full text-primary transition-colors ${searchOpen ? 'bg-primary/10' : 'hover:bg-primary/10'}`}
                                >
                                    {searchOpen ? <X size={22} /> : <Search size={22} />}
                                </button>

                                {searchOpen && (
                                    <div className="absolute right-0 top-full mt-3 w-[min(24rem,calc(100vw-2rem))] rounded-2xl bg-white/95 p-3 shadow-xl backdrop-blur-md">
                                        <DeferredSearchBoxHome className="!py-1.5 shadow-none" />
                                    </div>
                                )}
                            </div>

                            <div className="relative cursor-pointer" onClick={() => setLeadFrom(true)}>
                                <svg width="22" height="21" viewBox="0 0 22 21" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M15.8369 0C19.0298 0.000124093 21.6179 2.58839 21.6182 5.78125V11.8145C21.6182 15.0075 19.0299 17.5966 15.8369 17.5967H6.4873C6.2019 17.5967 5.92501 17.6938 5.70215 17.8721L2.8584 20.1465C1.70635 21.0676 0.000256631 20.2475 0 18.7725V5.78125C0.00021524 2.58845 2.58845 0.000214372 5.78125 0H15.8369ZM5.83105 7.5918C5.13711 7.59205 4.57422 8.15463 4.57422 8.84863C4.57443 9.54246 5.13724 10.1052 5.83105 10.1055C6.52508 10.1055 7.08866 9.54261 7.08887 8.84863C7.08887 8.15448 6.52521 7.5918 5.83105 7.5918ZM10.8604 7.5918C10.1662 7.5918 9.60352 8.15448 9.60352 8.84863C9.60373 9.54261 10.1663 10.1055 10.8604 10.1055C11.5542 10.1053 12.117 9.5425 12.1172 8.84863C12.1172 8.15459 11.5544 7.59198 10.8604 7.5918ZM15.8857 7.5918C15.1917 7.59198 14.6289 8.15459 14.6289 8.84863C14.6291 9.5425 15.1919 10.1053 15.8857 10.1055C16.5798 10.1055 17.1424 9.54261 17.1426 8.84863C17.1426 8.15448 16.5799 7.5918 15.8857 7.5918Z"
                                        fill="#0131B8"/>
                                </svg>
                            </div>

                            <div className="relative cursor-pointer" onClick={() => window.location.href = CONTACT_PHONE_HREF}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M17.8467 19.1314C15.6753 21.3029 10.1142 19.2625 5.42586 14.5741C0.737472 9.88576 -1.3029 4.32475 0.868559 2.15328L2.2983 0.723537C3.28533 -0.263494 4.91171 -0.237403 5.93093 0.781813L8.14547 2.99636C9.16469 4.01557 9.19078 5.64196 8.20375 6.62899L7.89666 6.93608C7.36374 7.46899 7.31161 8.32868 7.80678 8.92874C8.28441 9.50754 8.79933 10.0841 9.35764 10.6424C9.91595 11.2007 10.4925 11.7156 11.0713 12.1932C11.6713 12.6884 12.531 12.6363 13.0639 12.1033L13.371 11.7963C14.358 10.8092 15.9844 10.8353 17.0036 11.8545L19.2182 14.0691C20.2374 15.0883 20.2635 16.7147 19.2765 17.7017L17.8467 19.1314Z"
                                        fill="#0131B8"/>
                                </svg>
                            </div>


                            <div className="relative cursor-pointer" onClick={toggleCart}>
                                <svg width="27" height="25" viewBox="0 0 27 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M0.018375 1.34353C0.120035 0.83576 0.614074 0.506618 1.12189 0.608181L2.43341 0.870876C3.91537 1.16735 5.03108 2.39634 5.18146 3.90017L5.28791 4.96658H22.2625C24.7435 4.96668 26.5643 7.29793 25.9627 9.70486L24.5584 15.3211C23.9668 17.6864 21.8413 19.3453 19.4031 19.3455H8.15802C6.41753 19.3454 4.95148 18.0452 4.74396 16.3172L3.50666 6.00662L3.31427 4.0867C3.24588 3.4033 2.73965 2.84461 2.06623 2.70974L0.753727 2.44705C0.245995 2.3454 -0.0831121 1.85131 0.018375 1.34353Z"
                                        fill="#0131B8"/>
                                    <circle cx="9.06507" cy="22.7847" r="1.87562" fill="#0131B8"/>
                                    <circle cx="20.319" cy="22.7847" r="1.87562" fill="#0131B8"/>
                                </svg>
                                {isClient && cartCount > 0 && (
                                    <Badge className="absolute -top-2 -right-2 !px-2 text-white border-0">
                                        {cartCount}
                                    </Badge>
                                )}

                                {cartOpen && (
                                    <div
                                        className="absolute right-1/2 top-[100%] mt-2 w-64 rounded-lg bg-white/90 p-1.5 shadow-lg z-10 transition-opacity duration-200"
                                    >
                                        {vehiclesInCart.length === 0 ? (
                                            <div className="text-center p-4">
                                                <p className="text-sm font-bold text-black">Il carrello è vuoto</p>
                                            </div>
                                        ) : (
                                            vehiclesInCart.map((vehicle: VeicoloData, index) => (
                                                <div key={index}
                                                     className="rounded bg-white flex justify-between items-center p-2">
                                                    <div className="flex-none relative">

                                                        <p className="absolute top-0 left-0 text-[10px] px-1 text-[#979797]">1x</p>

                                                        <Image
                                                            src={vehicle.images.find((image) => image.defaultimage == '1')?.image || '/autoinarrivo.webp'}
                                                            alt={vehicle.marca + ' ' + vehicle.modello}
                                                            className="rounded-lg object-cover"
                                                            width={50} // Adjusted to better match the intended width
                                                            height={50} // Adjusted to keep aspect ratio
                                                            style={{
                                                                width: '50px',
                                                                height: 'auto'
                                                            }} // Responsive styling
                                                        />
                                                    </div>

                                                    <div className="flex flex-col justify-center flex-grow pl-2">
                                                <span className="text-xs leading-tight font-light">
                                                    {vehicle.marca}
                                                </span>
                                                        <span className="text-sm font-bold">{vehicle.modelloAbr}</span>
                                                    </div>

                                                    <div className="flex-none">
                        <span
                            className="text-sm font-normal">{formatPrice(+vehicle.prezzo)}</span>
                                                    </div>
                                                </div>
                                            ))
                                        )}

                                        {vehiclesInCart.length > 0 && (
                                            <div className="w-full pt-2 rounded">
                                                <Link onClick={toggleCart}
                                                      hrefLang={'it'}
                                                      href={checkoutUrl}
                                                      title={'Carrello'}
                                                      className="block w-full rounded text-white bg-gradient p-1 text-center"
                                                >
                                                    Vai al checkout
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="relative cursor-pointer" onClick={handleAccount}>
                                {userData && backOfficeName ? (
                                    <span className="text-sm font-bold text-[#000D7D] whitespace-nowrap">
                                        {backOfficeName}
                                    </span>
                                ) : (
                                    <svg className="w-8 h-8" width="45" height="44" viewBox="0 0 45 44" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <g clipPath="url(#clip0_578_51)">
                                            <rect x="0.222656" width="44" height="44" rx="22" fill={isAuthenticated ? "#000D7D" : "#000D7D"}/>
                                            <circle cx="8.11429" cy="8.11429" r="8.11429"
                                                    transform="matrix(-1 0 0 1 30.337 11)" fill="#A6C2FF"/>
                                            <path d="M8.02344 39.176C8.02344 37.4856 9.08612 35.9776 10.6781 35.4091L11.1243 35.2497C18.3017 32.6863 26.1451 32.6863 33.3225 35.2497L33.7688 35.4091C35.3608 35.9776 36.4234 37.4856 36.4234 39.176V44.194C36.4234 45.4111 35.3455 46.346 34.1406 46.1739L26.0418 45.0169C23.5091 44.6551 20.9378 44.6551 18.4051 45.0169L10.3063 46.1739C9.10141 46.346 8.02344 45.4111 8.02344 44.194V39.176Z"
                                                  fill="#A6C2FF"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_578_51">
                                                <rect x="0.222656" width="44" height="44" rx="22" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                )}
                            </div>



                        </div>
                    </div>
                </div>
            </nav>

            <NavbarMobile isScrolled={isScrolled}/>

            {
                leadFrom && (
                    <DeferredFormLead textarea={true} articolo={''} title={'Chatta con noi'} marca={''} modello={''}
                              setOpen={setLeadFrom} open={leadFrom} phone={telephone} whatsappMessage="Salve, vorrei avere maggiori informazioni."/>
                )
            }
        </>
    );
};

export default Navbar;
